import { Globe } from 'lucide-react'
import { type Control, Controller, type FieldErrors, type UseFormWatch } from 'react-hook-form'

import { MultiSelectAutocomplete, type SelectOption } from '@/components/ui/MultiSelectAutocomplete'
import {
  type CreateUserFormData,
  type UpdateUserFormData,
  type UserFormInput,
} from '@/lib/validation/user-schemas'

import { getSystemsPlaceholder } from './retailAccessUtils'

interface SystemsFieldProps {
  control: Control<UserFormInput, unknown, CreateUserFormData | UpdateUserFormData>
  watch: UseFormWatch<UserFormInput>
  errors: FieldErrors<UserFormInput>
  isRootOperator: boolean
  systemOptions: SelectOption[]
}

export const SystemsField = ({
  control,
  watch,
  errors,
  isRootOperator,
  systemOptions,
}: SystemsFieldProps) => (
  <div className="space-y-3">
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-2">
        <span className="text-gray-500 dark:text-gray-400">
          <Globe className="w-4 h-4" />
        </span>
        <label
          htmlFor="systems_all"
          className={`text-sm font-medium ${isRootOperator ? 'text-gray-400 dark:text-gray-600' : 'text-gray-700 dark:text-gray-300'}`}
        >
          Systems
        </label>
      </div>
      <div className="flex items-center">
        <Controller
          name="attributes.systems_all"
          control={control}
          render={({ field }) => (
            <input
              type="checkbox"
              id="systems_all"
              checked={field.value || false}
              onChange={(e) => {
                field.onChange(e.target.checked)
              }}
              onBlur={field.onBlur}
              ref={field.ref}
              disabled={isRootOperator}
              className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 bg-white dark:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
            />
          )}
        />
        <label
          htmlFor="systems_all"
          className={`ml-2 text-sm ${isRootOperator ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed' : 'text-gray-600 dark:text-gray-400'}`}
        >
          Grant access to all
        </label>
      </div>
    </div>

    <div>
      <Controller
        name="attributes.systems"
        control={control}
        render={({ field }) => {
          const value = Array.isArray(field.value) ? field.value : []
          const systemsAll = watch('attributes.systems_all')

          return (
            <MultiSelectAutocomplete
              options={systemOptions}
              value={value}
              onChange={(newValue) => {
                field.onChange(newValue)
              }}
              placeholder={getSystemsPlaceholder(isRootOperator, systemsAll)}
              disabled={systemsAll || isRootOperator}
            />
          )
        }}
      />
      <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
        System IDs this user can access
      </p>
      {errors.attributes?.systems && (
        <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">
          {errors.attributes.systems.message}
        </p>
      )}
    </div>
  </div>
)
